import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { ShieldCheck, Loader2, AlertTriangle, RotateCcw } from 'lucide-react';

/**
 * Admin → Settings → Maintenance tab card.
 * Fires the admin-activated carve-out backfill.
 *
 * Flow: click Preview → shows dry-run counts in a modal → click Apply
 * to write the changes. Idempotent — safe to run multiple times.
 */
export function AdminActivatedBackfillCard() {
  const { api } = useAuth();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [lastRun, setLastRun] = useState(null);

  const openPreview = async () => {
    setPreviewOpen(true);
    setPreview(null);
    setPreviewLoading(true);
    try {
      const { data } = await api.get('/api/admin/admin-activated-backfill/preview');
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
      setPreviewOpen(false);
    }
    setPreviewLoading(false);
  };

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post('/api/admin/admin-activated-backfill/apply');
      setLastRun(data);
      setPreviewOpen(false);
      toast.success(
        `Backfill complete — flagged ${data.flagged || 0}, reversed ${data.commissions_reversed || 0} commission(s).`,
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backfill failed');
    }
    setApplying(false);
  };

  return (
    <>
      <Card className="bg-[#041A3B] border-amber-400/30" data-testid="admin-activated-backfill-card">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2 text-base">
            <ShieldCheck className="w-5 h-5 text-amber-400" />
            Admin-Activation Carve-Out Backfill
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-[#A0B4CC] text-xs leading-relaxed">
            Flags legacy members whose subscription was activated by an admin (no real payment)
            so their upline stops earning bonuses on them. Reverses any commissions already
            paid out on those members and recomputes affected stock certificates. Idempotent.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={openPreview}
              className="bg-amber-400/15 border border-amber-400/40 text-amber-400 hover:bg-amber-400/25"
              data-testid="backfill-preview-btn"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Preview backfill
            </Button>
          </div>
          {lastRun && (
            <div className="mt-3 rounded-md bg-[#000B18]/60 border border-white/10 p-3 text-xs text-[#A0B4CC] space-y-1" data-testid="backfill-last-run">
              <p className="text-white font-medium">Last run</p>
              <p>Flagged: <span className="text-amber-400 font-mono">{lastRun.flagged}</span></p>
              <p>Commissions reversed: <span className="text-[#00E676] font-mono">{lastRun.commissions_reversed}</span> (₱{Number(lastRun.commission_amount_php || 0).toLocaleString()})</p>
              <p>Uplines recomputed: <span className="text-[#00E5FF] font-mono">{lastRun.uplines_recomputed}</span></p>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-[#041A3B] border-white/10 max-w-md" data-testid="backfill-preview-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Preview backfill run
            </DialogTitle>
            <DialogDescription className="text-[#A0B4CC] text-xs">
              This will flag legacy admin-activated members, reverse over-credited referral
              commissions, and recompute affected stock certificates. Nothing is written until
              you confirm.
            </DialogDescription>
          </DialogHeader>
          {previewLoading || !preview ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-amber-400" /></div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {[
                  { l: 'Users to flag', v: preview.detected_user_count, c: 'text-amber-400', t: 'backfill-detected-count' },
                  { l: 'Commissions to reverse', v: preview.commissions_to_reverse, c: 'text-[#00E676]', t: 'backfill-commissions-count' },
                  { l: 'PHP to debit uplines', v: `₱${Number(preview.commission_amount_php || 0).toLocaleString()}`, c: 'text-[#FF6B6B]', t: 'backfill-amount' },
                  { l: 'Uplines to recompute', v: preview.uplines_to_recompute, c: 'text-[#00E5FF]', t: 'backfill-uplines-count' },
                ].map(x => (
                  <div key={x.l} className="p-3 rounded-md bg-[#000B18]/60 border border-white/5" data-testid={x.t}>
                    <p className="text-[10px] uppercase tracking-widest text-[#5C7599]">{x.l}</p>
                    <p className={`font-mono text-lg font-bold ${x.c}`}>{x.v}</p>
                  </div>
                ))}
              </div>
              {preview.sample_emails && preview.sample_emails.length > 0 && (
                <div className="rounded-md bg-[#000B18]/60 border border-white/5 p-3">
                  <p className="text-[10px] uppercase tracking-widest text-[#5C7599] mb-2">Sample emails</p>
                  <ul className="text-xs text-[#A0B4CC] space-y-1 max-h-32 overflow-y-auto">
                    {preview.sample_emails.map(e => <li key={e} className="break-all">· {e || '(no email)'}</li>)}
                  </ul>
                </div>
              )}
              <div className="flex gap-2 justify-end pt-2 border-t border-white/5">
                <Button variant="ghost" onClick={() => setPreviewOpen(false)} className="text-[#A0B4CC]" data-testid="backfill-cancel-btn">
                  Cancel
                </Button>
                <Button
                  onClick={apply}
                  disabled={applying || preview.detected_user_count === 0}
                  className="bg-amber-400 text-[#001627] hover:bg-amber-500"
                  data-testid="backfill-apply-btn"
                >
                  {applying ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Apply backfill
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AdminActivatedBackfillCard;
